import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, BellRing, CheckCircle2 } from "lucide-react";
import { formatDateTimeIt } from "@/lib/format";
import { REM } from "@/constants/testIds";
import ReminderDialog from "@/components/app/ReminderDialog";
import { toast } from "sonner";

const TABS = [
  { value: "today", label: "Oggi" },
  { value: "overdue", label: "Scaduti" },
  { value: "upcoming", label: "Prossimi" },
  { value: "week", label: "Settimana" },
  { value: "completed", label: "Completati" },
  { value: "all", label: "Tutti" },
];

const priorityClass = (p) =>
  p === "alta"
    ? "border-red-200 bg-red-50 text-red-700"
    : p === "bassa"
    ? "border-stone-200 bg-stone-50 text-stone-600"
    : "border-amber-200 bg-amber-50 text-amber-700";

export default function Richiami() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const tab = params.get("tab") || "today";

  const load = useCallback(async () => {
    const r = await api.get(`/reminders`, { params: { filter: tab } });
    setItems(r.data);
  }, [tab]);
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("crm:data-changed", h);
    return () => window.removeEventListener("crm:data-changed", h);
  }, [load]);

  const toggle = async (id) => {
    await api.post(`/reminders/${id}/toggle`);
    load();
  };
  const del = async (id) => {
    await api.delete(`/reminders/${id}`);
    toast.success("Richiamo eliminato");
    load();
  };

  const setTab = (v) => {
    const p = new URLSearchParams(params);
    p.set("tab", v);
    setParams(p);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">Agenda</div>
          <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Richiami</h1>
          <p className="text-stone-500 mt-1">Tutti i promemoria programmati e completati.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 shadow-sm">
          <Plus className="w-4 h-4" /> Nuovo Richiamo
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 overflow-x-auto">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={REM.filterTab(t.value)}
              className="data-[state=active]:bg-white data-[state=active]:text-[#F56A1F]"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-5">
          {items.length === 0 ? (
            <Card className="border-dashed border-stone-200 rounded-2xl p-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-100 flex items-center justify-center mb-3">
                <BellRing className="w-6 h-6 text-stone-400" />
              </div>
              <div className="text-stone-500">Nessun richiamo in questa vista.</div>
            </Card>
          ) : (
            <div className="space-y-2">
              {items.map((r) => (
                <Card
                  key={r.id}
                  data-testid={REM.row(r.id)}
                  className={`border-stone-200 rounded-xl p-4 flex items-start gap-3 hover:border-stone-300 transition-colors ${
                    r.completed ? "opacity-60" : ""
                  }`}>
                  <button
                    data-testid={REM.toggle(r.id)}
                    onClick={() => toggle(r.id)}
                    className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      r.completed ? "bg-emerald-500 border-emerald-500" : "border-stone-300 hover:border-[#F56A1F]"
                    }`}>
                    {r.completed && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`font-medium ${r.completed ? "line-through text-stone-400" : "text-stone-900"}`}>
                        {r.titolo}
                      </div>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${priorityClass(r.priorita)}`}>
                        {r.priorita}
                      </Badge>
                    </div>
                    {r.client_ragione_sociale && (
                      <button
                        onClick={() => navigate(`/clienti/${r.client_id}`)}
                        className="text-xs text-[#F56A1F] mt-1 hover:underline">
                        → {r.client_ragione_sociale}
                      </button>
                    )}
                    {r.descrizione && <div className="text-sm text-stone-500 mt-1">{r.descrizione}</div>}
                    <div className="text-xs text-stone-400 font-mono mt-1">{formatDateTimeIt(r.scheduled_at)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={REM.edit(r.id)}
                      onClick={() => { setEditing(r); setOpen(true); }}
                      className="text-stone-400 hover:text-stone-900 p-1.5 rounded hover:bg-stone-100">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(r.id)}
                      className="text-stone-400 hover:text-red-500 p-1.5 rounded hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ReminderDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
}
